import { useState } from "react";

// One entry per page after the first: the cursor that fetches it and how
// many rows came before it. Pages can come back short (a server-side filter
// may stop at its work budget), so offsets are tracked, not computed.
interface PageMark {
  cursor: string;
  offset: number;
}

export interface CursorPaging {
  // 1-based page number.
  page: number;
  // Cursor for the current page; undefined on page one.
  cursor?: string;
  // Rows on the pages before this one.
  offset: number;
  // Advance using the server's cursor and the size of the page on screen.
  next: (cursor: string, pageCount: number) => void;
  prev: () => void;
  first: () => void;
}

// useCursorPaging tracks Prev/Next over a cursor-paginated list. Paging
// restarts whenever resetKey changes — pass something that captures the
// filters and page size, so a stale cursor is never sent with new filters.
export function useCursorPaging(resetKey: string): CursorPaging {
  const [state, setState] = useState<{ key: string; marks: PageMark[] }>({ key: resetKey, marks: [] });
  const marks = state.key === resetKey ? state.marks : [];
  const current = marks[marks.length - 1];

  return {
    page: marks.length + 1,
    cursor: current?.cursor,
    offset: current?.offset ?? 0,
    next: (cursor, pageCount) =>
      setState({ key: resetKey, marks: [...marks, { cursor, offset: (current?.offset ?? 0) + pageCount }] }),
    prev: () => setState({ key: resetKey, marks: marks.slice(0, -1) }),
    first: () => setState({ key: resetKey, marks: [] }),
  };
}

// formatRange renders "26–30 of 30" for a page. total is undefined while
// unknown ("…"); a lower-bound total gets a "+".
export function formatRange(offset: number, count: number, total?: number, totalIsLowerBound = false): string {
  const totalLabel = total == null ? "…" : `${total.toLocaleString()}${totalIsLowerBound ? "+" : ""}`;
  if (count === 0) return `0 of ${totalLabel}`;
  return `${(offset + 1).toLocaleString()}–${(offset + count).toLocaleString()} of ${totalLabel}`;
}

// totalPagesFor returns the page count when the total is known, or
// undefined so pagers say "Page N" instead of inventing a count.
export function totalPagesFor(page: number, pageSize: number, hasMore: boolean, total?: number): number | undefined {
  if (total == null) return undefined;
  if (!hasMore) return page;
  return Math.max(page + 1, Math.ceil(total / pageSize));
}
